import { useState, useEffect, useCallback, useMemo } from "react";
import { SESSION_USER_CHANGED_EVENT, CURRENT_USER_STORAGE_KEY } from "../utils/tenantContext";

/**
 * useCurrentUser
 *
 * A centralized hook that reads the full logged-in user session from
 * localStorage and exposes each piece of data cleanly.
 *
 * Reactively updates if localStorage changes (e.g. after re-login).
 *
 * @returns {Object} Full session data with convenient named accessors
 *
 * @example
 * // Full object destructuring
 * const { user, tenant, modules, role, token, tenantId, isAdmin } = useCurrentUser();
 *
 * // Access a specific module's permissions
 * const { getModule, hasPermission } = useCurrentUser();
 * const dashboardModule = getModule("DASHBOARD");
 * const canCreate = hasPermission("EMPLOYEES", "canCreate");
 */
const useCurrentUser = () => {
    const readFromStorage = useCallback(() => {
        try {
            return JSON.parse(localStorage.getItem(CURRENT_USER_STORAGE_KEY) || "{}");
        } catch {
            return {};
        }
    }, []);

    const [data, setData] = useState(readFromStorage);

    // Re-sync on cross-tab storage changes and same-tab login/logout events.
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === CURRENT_USER_STORAGE_KEY) {
                setData(readFromStorage());
            }
        };
        const handleSessionChange = () => {
            setData(readFromStorage());
        };
        window.addEventListener("storage", handleStorageChange);
        window.addEventListener(SESSION_USER_CHANGED_EVENT, handleSessionChange);
        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener(SESSION_USER_CHANGED_EVENT, handleSessionChange);
        };
    }, [readFromStorage]);

    // ─── Derived values ───────────────────────────────────────────────────────────

    /** The core user object (id, firstName, lastName, email, ssn, address, etc.) */
    const user = {
        id: data.id,
        userId: data.id,
        firstName: data.firstName,
        lastName: data.lastName,
        fullName: [data.firstName, data.lastName].filter(Boolean).join(" "),
        userName: data.userName,
        email: data.email,
        mobileNumber: data.mobileNumber,
        dateOfBirth: data.dateOfBirth,
        ssn: data.ssn,
        address: data.address,
        street: data.street,
        suiteApt: data.suiteApt,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
        country: data.country,
        workMode: data.workMode,
        hireDate: data.hireDate,
        status: data.status,
        roleId: data.roleId,
        // Keep role for permission helpers (resolveUserRoleName / moduleAccess)
        role: data.role ?? null,
        roleName:
            typeof data.role === "string"
                ? data.role
                : data.role?.name ?? null,
    };
    const isAdminRole = data?.roleId === 1;
    const isSurveyRole = data?.roleId === 12;

    /** The tenant object from the login response */
    const tenant = data.tenant || null;
    const officeLogo = tenant?.officeLogo || null;

    /** The tenantId (top-level from login response) */
    const tenantId = data.tenantId || data.tenant?.id || null;

    const companyName = tenant?.companyName || null;

    const seniorStaffNurse = tenant?.seniorStaffNurse || "";

    /** The role string ("Admin", "Employee", etc.) */
    let role = null;
    if (typeof data.role === "string") {
        role = data.role;
    } else if (typeof data.role === "object" && data.role !== null) {
        role = data.role?.name ?? null;
    }

    /** The role object (id, name, description) — from the nested user.role field */
    const roleObject = typeof data.role === "object" ? data.role : null;

    /** Whether the current user is an Admin */
    const isAdmin =
        role?.toLowerCase() === "admin" ||
        data.roleId === 1 ||
        roleObject?.id === 1;

    const isSuperAdmin =
        role === "SuperAdmin" ||
        String(role || "").toLowerCase() === "superadmin";

    /** The JWT token */
    const token = data.token || null;

    /** The modules array with permissions */
    const modules = useMemo(() => Array.isArray(data.modules) ? data.modules : [], [data.modules]);

    // ─── Helper functions ─────────────────────────────────────────────────────────

    /**
     * Get a single module by its code.
     * @param {string} code - e.g. "DASHBOARD", "EMPLOYEES", "APPLICATION"
     * @returns {Object|null} The module object or null
     *
     * @example
     * const empModule = getModule("EMPLOYEES");
     * // { id: 2, name: "Employees", code: "EMPLOYEES", permissions: { canView, canCreate, canUpdate, canDelete } }
     */
    const getModule = useCallback(
        (code) => modules.find((m) => m.code === code) || null,
        [modules]
    );

    /**
     * Check a specific permission for a module.
     * @param {string} moduleCode - e.g. "EMPLOYEES"
     * @param {"canView"|"canCreate"|"canUpdate"|"canDelete"} permission
     * @returns {boolean}
     *
     * @example
     * const canDelete = hasPermission("EMPLOYEES", "canDelete");
     */
    const hasPermission = useCallback(
        (moduleCode, permission) => {
            const mod = modules.find((m) => m.code === moduleCode);
            return mod?.permissions?.[permission] === true;
        },
        [modules]
    );

    /**
     * Get a flat map of all module permissions.
     * @returns {Record<string, Object>} e.g. { DASHBOARD: { canView, canCreate, ... }, ... }
     */
    const allPermissions = modules.reduce((acc, mod) => {
        acc[mod.code] = mod.permissions || {};
        return acc;
    }, {});

    /**
     * Refresh data from localStorage manually (useful after a silent re-login).
     */
    const refresh = useCallback(() => {
        setData(readFromStorage());
    }, [readFromStorage]);

    return {
        // ── Core data ──────────────────────────────────────────────────────────────
        /** Full user info object */
        user,
        /** Tenant object { id, name, companyName, timeZone, domain, status } */
        tenant,
        /** Numeric tenantId */
        tenantId,
        /** Role string e.g. "Admin" */
        role,
        /** Role object with id/name/description */
        roleObject,
        /** true if the current user is an Admin */
        isAdmin,
        isSuperAdmin,
        /** JWT token string */
        token,
        /** Array of modules with permissions */
        modules,
        /** All permissions keyed by module code */
        allPermissions,

        // ── Convenience shortcuts ──────────────────────────────────────────────────
        /** User's numeric id */
        userId: data.id,
        /** User's firstName */
        firstName: data.firstName,
        /** User's lastName */
        lastName: data.lastName,
        /** User's full name "First Last" */
        fullName: [data.firstName, data.lastName].filter(Boolean).join(" "),
        /** User's email */
        email: data.email,
        /** User's SSN */
        ssn: data.ssn,
        /** User's primary address */
        address: data.address,
        /** User's mobile number */
        mobileNumber: data.mobileNumber,
        /** User's date of birth */
        dateOfBirth: data.dateOfBirth,
        /** User's date of hire */
        hireDate: data.hireDate,

        // ── Helper functions ───────────────────────────────────────────────────────
        /** Get a module object by code (e.g. "DASHBOARD") */
        getModule,
        /** Check a permission for a module (e.g. hasPermission("EMPLOYEES", "canCreate")) */
        hasPermission,
        /** Manually re-read from localStorage */
        refresh,
        companyName,
        seniorStaffNurse,
        isAdminRole,
        officeLogo,
        isSurveyRole
    };
};

export default useCurrentUser;
